import React from 'react';
import Link from 'next/link';

const XlimIntroSection = ({ variant = 'designer' }) => {
  const dynamicCapabilities = [
    {
      title: "Dynamic Text & Typography",
      description: "Insert variable text with full control over fonts, paragraph styles, and typographic settings. Each instance maintains professional quality.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      )
    },
    {
      title: "Dynamic Images",
      description: "Place images from asset libraries or live URLs. Images automatically scale and crop within frames as designed.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: "Data-Driven Styles",
      description: "Apply paragraph and object styles automatically based on data, ensuring design consistency across every variation.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      )
    },
    {
      title: "Conditional Layers & Spreads",
      description: "Switch layers or entire spreads on or off based on data, creating targeted versions within a single template.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },
    {
      title: "Dynamic Barcodes",
      description: "Generate QR codes and barcodes directly from live data and place them anywhere in your design.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
      )
    },
    {
      title: "Multi-Page Automation",
      description: "Produce personalised brochures, catalogues, or reports with data controlling content across every page.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    }
  ];

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white overflow-hidden relative">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-primary opacity-5 rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            Creative Control, <span className="text-primary">Powered by Data</span>
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed">
            When you design for Personalia, you're designing with <strong className="text-white">XLIM</strong> - our rendering engine that turns your Adobe InDesign layouts into dynamic, personalised masterpieces. Your creative process stays exactly where you want it: inside InDesign, with your original file always remaining the source of truth.
          </p>
        </div>

        {/* Static vs Dynamic explanation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* Static Elements */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity blur-sm"></div>
            <div className="relative bg-gray-800 rounded-2xl p-6 border border-gray-700 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-blue-500 bg-opacity-20 p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-blue-400">Static Elements</h4>
                <span className="ml-auto text-xs bg-blue-500 bg-opacity-20 text-blue-300 px-3 py-1 rounded-full">100% Fidelity</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Any part of your design that remains constant is rendered with <strong className="text-white">full InDesign fidelity</strong>. Use the most complex features for backgrounds, brand assets, and fixed layouts.
              </p>
            </div>
          </div>

          {/* Dynamic Elements */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-primary to-orange-500 rounded-2xl opacity-20 group-hover:opacity-30 transition-opacity blur-sm"></div>
            <div className="relative bg-gray-800 rounded-2xl p-6 border border-gray-700 h-full">
              <div className="flex items-center gap-3 mb-3">
                <div className="bg-primary bg-opacity-20 p-2 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-primary">Dynamic Elements</h4>
                <span className="ml-auto text-xs bg-primary bg-opacity-20 text-red-300 px-3 py-1 rounded-full">Data-Driven</span>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                When you link an object to data, XLIM simulates InDesign's rendering—supporting <strong className="text-white">professional typography, styles, colours, and layout logic</strong> like auto-flow and copyfitting.
              </p>
            </div>
          </div>
        </div>

        {/* Dynamic Capabilities Grid */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-center mb-8">What You Can Make Dynamic</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {dynamicCapabilities.map((capability, index) => (
              <div key={index} className="bg-white bg-opacity-5 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-10 hover:bg-opacity-10 transition-all">
                <div className="flex items-start gap-4">
                  <div className="bg-primary bg-opacity-20 p-2 rounded-lg text-primary flex-shrink-0">
                    {capability.icon}
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{capability.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{capability.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preflight Tool Section */}
        <div className="bg-gradient-to-r from-primary to-red-600 rounded-2xl p-1 mb-12">
          <div className="bg-gray-900 rounded-xl p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0">
                <div className="bg-primary bg-opacity-20 p-3 rounded-xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-xl md:text-2xl font-bold mb-3">
                  Stay in the Green Zone: <span className="text-primary">The Preflight Tool</span>
                </h3>
                <p className="text-gray-300 leading-relaxed mb-4">
                  The XLIM Preflight panel is your real-time assistant, providing <strong className="text-white">live creative guidance</strong> and <strong className="text-white">instant validation</strong> to ensure your design is optimised for the Personalia API before you even finish it.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 bg-green-500 bg-opacity-20 text-green-300 px-3 py-1 rounded-full text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Live Feedback
                  </span>
                  <span className="inline-flex items-center gap-2 bg-green-500 bg-opacity-20 text-green-300 px-3 py-1 rounded-full text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Feature Validation
                  </span>
                  <span className="inline-flex items-center gap-2 bg-green-500 bg-opacity-20 text-green-300 px-3 py-1 rounded-full text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    API-Ready Output
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Professional Output & CTA */}
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-gray-300 leading-relaxed mb-6">
            Every piece generated by Personalia—whether <strong className="text-white">PDF, PNG, or JPG</strong>—perfectly reflects your original creative intent with production-ready quality including CMYK colours, bleeds, and professional print settings.
          </p>
          {/*
           <div className="flex flex-wrap justify-center gap-4">
            <Link href="/xlim-support" className="btn btn-primary btn-lg">
              Explore XLIM Features
            </Link>
            <Link href="/contact" className="btn btn-outline-light btn-lg">
              Get Started
            </Link>
          </div>
          */}
        </div>
      </div>
    </section>
  );
};

export default XlimIntroSection;
